import { createLocalStorageProvider } from '@jira/platform__browser-storage-providers/src/controllers/local-storage/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export type ViewMode = 'MODAL' | 'SIDEBAR' | 'DEFAULT';

// The storage key and provider are private to this package. Reads and writes of the issue
// preview-mode preference go exclusively through getViewModeFromLocalStorage and
// setViewModeInLocalStorage, so the key string lives in exactly one place and cannot drift between
// the React context package and the @jira/issue__context-service that also persists this preference.
const localStorage = createLocalStorageProvider('jira_issue_view');
const VIEW_MODE_STORAGE_KEY = 'view-mode';

// Plain localStorage read with no SSR guard. When adopting in the issue view context, wrap the call
// in an SSR-safe guard (e.g. useSsrSafeCallback) to avoid a hydration mismatch.
export const getViewModeFromLocalStorage = (): ViewMode => {
	const stored = localStorage.get(VIEW_MODE_STORAGE_KEY);
	if (stored === 'MODAL' || stored === 'SIDEBAR') {
		return stored;
	}
	return 'DEFAULT';
};

// Written as a strictly necessary item because the preference is functional: a consent-blocked
// set() resolves to status BLOCKED and the provider then removes the key, so the user's choice
// would be silently discarded and every page load would fall back to the SIDEBAR default.
export const setViewModeInLocalStorage = (viewMode: 'MODAL' | 'SIDEBAR'): void => {
	if (fg('jira_wiv_view_mode_strictly_necessary_storage')) {
		// @browser-storage setter
		localStorage.setStrictlyNecessaryItem(VIEW_MODE_STORAGE_KEY, viewMode);
		return;
	}
	// @browser-storage setter
	localStorage.set(VIEW_MODE_STORAGE_KEY, viewMode);
};
